// ─────────────────────────────────────────────────────────────────────────
// renderAnalyticsReportPdf(model)
//
// Renders a buildAnalyticsReportPdfModel() output into a print-ready
// HTML document (portrait A4). This function ONLY renders — it never
// calculates and has no knowledge of the report's execution path. Same
// deterministic contract as CostControlPdfRenderer.js: same model in,
// same HTML out, always.
// ─────────────────────────────────────────────────────────────────────────

const BRAND = {
  navy: '#0F2744', blue: '#1B3A6B', blueLt: '#E8F0F9',
  green: '#1A7A4A', greenLt: '#E8F8EE',
  gray1: '#F4F7FA', gray2: '#E8EDF3', border: '#D8E0EA',
  text1: '#0F1E2D', text2: '#2C3E50', text3: '#5D7080', text4: '#8FA3B1',
  amber: '#B7770D', amberLt: '#FEF9E7',
}

function fmtValue(value, format) {
  if (value == null) return '—'
  if (format === 'percentage') return `${(value * 100).toFixed(1)}%`
  if (format === 'ratio') return value.toFixed(2)
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export function renderAnalyticsReportPdf(model) {
  const { header, metadata, measures, tableRows, notes } = model

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(header.reportTitle)}</title>
<style>
  @page { size: A4 portrait; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Arial, sans-serif; color: ${BRAND.text1}; margin: 0; }
  .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid ${BRAND.blue}; padding-bottom: 12px; margin-bottom: 16px; }
  .org-name { font-size: 13px; font-weight: 700; color: ${BRAND.blue}; }
  .report-title { font-size: 20px; font-weight: 800; margin: 4px 0; }
  .dataset-badge { display: inline-block; background: ${BRAND.blueLt}; color: ${BRAND.blue}; font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 12px; }
  .meta-row { font-size: 10.5px; color: ${BRAND.text3}; margin-top: 8px; }
  .filter-chip { display: inline-block; background: ${BRAND.gray1}; border: 1px solid ${BRAND.border}; font-size: 9.5px; padding: 2px 8px; border-radius: 10px; margin-right: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 10.5px; }
  th { background: ${BRAND.gray1}; text-align: right; padding: 6px 10px; font-weight: 700; color: ${BRAND.text3}; border-bottom: 1px solid ${BRAND.border}; }
  th:first-child, td:first-child { text-align: left; }
  td { padding: 6px 10px; border-bottom: 1px solid ${BRAND.gray2}; font-family: 'SF Mono', Consolas, monospace; }
  .notes { margin-top: 20px; padding: 12px; background: ${BRAND.amberLt}; border-radius: 6px; font-size: 11px; }
  .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid ${BRAND.border}; font-size: 9px; color: ${BRAND.text4}; display: flex; justify-content: space-between; }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div class="org-name">${escapeHtml(header.orgName)}</div>
      <div class="report-title">${escapeHtml(header.reportTitle)}</div>
    </div>
    <div class="dataset-badge">${escapeHtml(header.datasetLabel || '')}</div>
  </div>

  <div class="meta-row">
    ${metadata.dimension ? `Grouped by: <strong>${escapeHtml(metadata.dimension)}</strong> · ` : ''}
    Visual: <strong>${escapeHtml(metadata.visualType)}</strong>
  </div>
  ${metadata.filterSummary.length > 0 ? `
  <div class="meta-row">
    Filters: ${metadata.filterSummary.map(f => `<span class="filter-chip">${escapeHtml(f.label)}: ${escapeHtml(f.value)}</span>`).join('')}
  </div>` : ''}

  <table>
    <thead>
      <tr>
        <th>Item</th>
        ${measures.map(m => `<th>${escapeHtml(m.label)}</th>`).join('')}
      </tr>
    </thead>
    <tbody>
      ${tableRows.map(row => `
      <tr>
        <td>${escapeHtml(row.label)}</td>
        ${row.values.map((v, i) => `<td>${fmtValue(v, measures[i]?.format)}</td>`).join('')}
      </tr>`).join('')}
    </tbody>
  </table>

  ${notes ? `<div class="notes"><strong>Notes:</strong> ${escapeHtml(notes)}</div>` : ''}

  <div class="footer">
    <span>Generated ${fmtDate(header.generatedAt)}${header.generatedBy ? ` by ${escapeHtml(header.generatedBy)}` : ''}</span>
    <span>CostSpace Analytics Studio</span>
  </div>
</body>
</html>`
}

function escapeHtml(str) {
  if (str == null) return ''
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;')
}
